/**
 * The story preview's pop-out window (spec 009). Lives in the story editor,
 * which stays mounted while scene tabs are open, so edits made in a tab reach
 * the pop-out. The two windows talk over BroadcastChannel `sm-story-preview`:
 *
 * - `orientation-change` in both directions;
 * - `scene-update`: the open scene's in-memory state, debounced 300 ms (and
 *   every scene with local state when the pop-out says `popout-ready`);
 * - `close` (editor → pop-out) and `popout-closed` (pop-out → editor).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { draftSceneIds, previewSceneInput, useSceneStore } from '../stores/sceneStore';
import { PREVIEW_CHANNEL, type PreviewMessage } from '../utils/previewSources';
import type { OrientationMode, PopOutTarget } from '../components/StoryPreviewPane';

export const SCENE_UPDATE_DEBOUNCE_MS = 300;

export function useStoryPreviewPopout(
  storyId: number | null,
  orientation: OrientationMode,
  setOrientation: (mode: OrientationMode) => void,
) {
  const [poppedOut, setPoppedOut] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const setOrientationRef = useRef(setOrientation);
  setOrientationRef.current = setOrientation;

  const post = useCallback((message: PreviewMessage) => {
    try { channelRef.current?.postMessage(message); } catch { /* a closed channel: nothing listens */ }
  }, []);

  /** Every scene the editor holds unsaved state for. */
  const sendLocalScenes = useCallback(() => {
    const ids = new Set(draftSceneIds());
    const open = useSceneStore.getState().scene;
    if (open) ids.add(Number(open.id));
    ids.forEach((id) => {
      const scene = previewSceneInput(id);
      if (scene) post({ type: 'scene-update', sceneId: id, scene });
    });
  }, [post]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel(PREVIEW_CHANNEL);
    channelRef.current = bc;
    bc.onmessage = (ev: MessageEvent<PreviewMessage>) => {
      const m = ev.data;
      if (m?.type === 'popout-closed') {
        setPoppedOut(false);
        popupRef.current = null;
      } else if (m?.type === 'orientation-change' && m.orientation) {
        setOrientationRef.current(m.orientation);
      } else if (m?.type === 'popout-ready') {
        sendLocalScenes();
      }
    };
    return () => {
      // Leaving the story editor closes its pop-out
      try { bc.postMessage({ type: 'close' } satisfies PreviewMessage); } catch { /* closed */ }
      bc.close();
      channelRef.current = null;
    };
  }, [sendLocalScenes]);

  // Orientation follows the editor
  useEffect(() => {
    if (poppedOut) post({ type: 'orientation-change', orientation });
  }, [orientation, poppedOut, post]);

  // Edits reach the pop-out within a second, without a save
  useEffect(() => {
    if (!poppedOut) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = useSceneStore.subscribe(
      (s) => [s.scene, s.keyframes] as const,
      () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          const open = useSceneStore.getState().scene;
          if (!open) return;
          const id = Number(open.id);
          const scene = previewSceneInput(id);
          if (scene) post({ type: 'scene-update', sceneId: id, scene });
        }, SCENE_UPDATE_DEBOUNCE_MS);
      },
      { equalityFn: (a, b) => a[0] === b[0] && a[1] === b[1] },
    );
    return () => {
      unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [poppedOut, post]);

  // A pop-out closed by the window manager
  useEffect(() => {
    if (!poppedOut) return;
    const interval = setInterval(() => {
      if (popupRef.current?.closed) {
        setPoppedOut(false);
        popupRef.current = null;
      }
    }, 500);
    return () => clearInterval(interval);
  }, [poppedOut]);

  const popOut = useCallback((target: PopOutTarget) => {
    const params = new URLSearchParams();
    if (storyId) params.set('storyId', String(storyId));
    params.set('orientation', orientation);
    if (target.mode === 'scene') {
      params.set('sceneId', String(target.sceneId));
      if (target.sceneName) params.set('sceneName', target.sceneName);
    } else {
      params.set('mode', 'runner');
      if (target.storySceneId) params.set('storySceneId', String(target.storySceneId));
    }
    const w = window.open(`/story-preview?${params}`, 'sm-story-preview', 'width=480,height=780,menubar=no,toolbar=no');
    if (w) {
      popupRef.current = w;
      setPoppedOut(true);
    }
  }, [storyId, orientation]);

  const popBack = useCallback(() => {
    post({ type: 'close' });
    try { popupRef.current?.close(); } catch { /* already closed */ }
    popupRef.current = null;
    setPoppedOut(false);
  }, [post]);

  return { poppedOut, popOut, popBack };
}
